<template>
  <div class="review-destination">
    <step-header
      :title="headerTitle"
      :subtitle="locationName"
    />
    <div class="body">
      <section
        v-for="section in sections"
        :key="section.type"
        :class="section.type === 'PUBLIC' ? 'public-review' : 'private-review'"
      >
        <img
          :src="section.image"
          :alt="section.alt"
        >
        <p>{{ section.copy }}</p>
        <pp-button
          class="section-btn"
          :type="section.type === 'PUBLIC' ? 'primary' : 'secondary'"
          :text="section.cta"
          :data-cy="section.type === 'PUBLIC' ? 'leave-review' : 'leave-feedback'"
          :disabled="section.type === 'PUBLIC' && !routingResolved"
          :aria-busy="section.type === 'PUBLIC' && !routingResolved ? 'true' : null"
          @click="section.type === 'PUBLIC' ? onPublicReview() : onPrivateReview()"
        />
        <p
          v-if="section.type === 'PUBLIC' && !routingResolved"
          class="routing-pending"
          role="status"
          data-cy="review-routing-pending"
        >
          Finding where to send your review.
        </p>
        <div
          v-if="section.type === 'PUBLIC' && externalNavigationFailed"
          class="external-review-fallback"
          aria-live="polite"
          aria-atomic="true"
        >
          <a
            :href="externalReviewUrl"
            target="_blank"
            rel="noopener noreferrer"
            data-cy="external-review-fallback"
          >
            Continue to review site <span class="sr-only">(opens in new tab)</span>
          </a>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import StepHeader from '@/components/StepHeader/StepHeader.vue'
import { REVIEW_TYPE } from '@/constants'
import { trackView, trackClick } from '@/analytics'
import { redirectToExternalReview } from '@/utils/navigation'

export default {
  name: 'ReviewDestination',
  components: { StepHeader },
  data () {
    return {
      externalNavigationFailed: false
    }
  },
  computed: {
    ...mapGetters({
      headerTitle: 'reputation/headerTitle',
      locationName: 'reputation/locationName',
      selectedProviderName: 'reputation/selectedProviderName',
      practiceName: 'reputation/practiceName',
      wouldRecommend: 'reputation/wouldRecommend',
      externalReviewUrl: 'reputation/externalReviewUrl',
      routingResolved: 'reputation/routingResolved',
      analyticsDims: 'reputation/analyticsDims'
    }),
    providerDisplayName () {
      return this.selectedProviderName || this.practiceName || 'our practice'
    },
    sections () {
      const publicSection = {
        type: 'PUBLIC',
        image: 'https://ui-cdn.patientpop.com/assets/img/patient-survey.svg',
        alt: '',
        copy: `Review your experience online with ${this.providerDisplayName} for future patients to see.`,
        cta: 'Leave review'
      }
      const privateSection = {
        type: 'PRIVATE',
        image: 'https://ui-cdn.patientpop.com/assets/img/insights.svg',
        alt: '',
        copy: 'If you have concerns you wish to address in private, please get in touch.',
        cta: 'Leave feedback'
      }
      return this.wouldRecommend === false
        ? [privateSection, publicSection]
        : [publicSection, privateSection]
    }
  },
  created () {
    trackView(this, 'review_options', this.analyticsDims)
    this.trackCampaignResponse()
  },
  methods: {
    ...mapActions({
      setReviewType: 'reputation/setReviewType',
      trackCampaignResponse: 'reputation/trackCampaignResponse'
    }),
    onPublicReview () {
      if (!this.routingResolved) return
      trackClick(this, 'public_review_selected', this.analyticsDims)
      if (this.externalReviewUrl) {
        this.externalNavigationFailed = redirectToExternalReview(this.externalReviewUrl) === false
        return
      }
      this.setReviewType(REVIEW_TYPE.PUBLIC)
      this.$router.push({ name: 'FeedbackForm' })
    },
    onPrivateReview () {
      trackClick(this, 'private_review_selected', this.analyticsDims)
      this.setReviewType(REVIEW_TYPE.PRIVATE)
      this.$router.push({ name: 'FeedbackForm' })
    }
  }
}
</script>

<style lang="scss" scoped>
.private-review {
  background: #ebebeb;
}

.body {
  display: flex;
  flex-direction: column;
}

section {
  padding: 24px 20px;

  & + section {
    border-top: 1px solid #e5e5e5;
  }
}

section img {
  display: block;
  height: 56px;
  margin-bottom: 12px;
  object-fit: contain;
  width: 56px;
}

section p {
  color: $text-primary;
  font-size: 15px;
  line-height: 1.4;
  margin: 0 0 14px;
}

.section-btn {
  display: block;
  height: 36px;
  width: 220px;
}

.routing-pending {
  color: $text-muted;
  font-size: 13px;
  margin: 8px 0 0;
}

.external-review-fallback {
  font-size: 15px;
}

.sr-only {
  clip: rect(0, 0, 0, 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}
</style>
